import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";

export type DeeplinkConversationResolution =
  | { action: "wait" }
  | { action: "select"; conversation: LazoChatConversationShell }
  | { action: "lookup" };

type Params = {
  conversationId: string | undefined;
  placeholderId: string;
  chats: LazoChatConversationShell[];
  urlTenantExternalId: string | null;
  currentTenantExternalId: string | undefined;
  ownedTenantExternalIds: string[];
};

// Decides what to do with a conversation opened from a notification deeplink.
//
// - "wait": no concrete conversation requested, or the deeplink points at a company the user owns but
//   hasn't been switched to yet (the tenant switch + chat-list refetch are in flight). Waiting avoids a
//   false access error against the wrong company's list.
// - "lookup": the conversation is not in the currently loaded chat page (it may be on a later page, or the
//   list is for a tenant still loading). The caller must verify it against the backend by id before showing
//   any access error — never deny based only on the paginated/in-flight list.
// - "select": the conversation is present and can be opened.
export function resolveDeeplinkConversation({
  conversationId,
  placeholderId,
  chats,
  urlTenantExternalId,
  currentTenantExternalId,
  ownedTenantExternalIds,
}: Params): DeeplinkConversationResolution {
  if (!conversationId || conversationId === placeholderId) {
    return { action: "wait" };
  }

  const tenantSwitchPending =
    !!urlTenantExternalId && urlTenantExternalId !== currentTenantExternalId && ownedTenantExternalIds.includes(urlTenantExternalId);
  if (tenantSwitchPending) {
    return { action: "wait" };
  }

  const conversation = chats.find((c) => c.externalId === conversationId);
  if (!conversation) {
    return { action: "lookup" };
  }

  return { action: "select", conversation };
}
